import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./AccountErrorRecovery.css";

// Rendered by RequireGender.js in place of its usual blank (null) render,
// ONLY for ACCOUNT_REASON_ERROR (a thrown AccountSessionStateContext
// read) - every other blocked reason already has a real redirect target
// of its own and is unaffected. `onRetry` is AccountSessionStateContext's
// own refresh() - this component calls nothing else: no capture, upload,
// or submitVerificationRequest call exists at this level to repeat, by
// construction (RequireGender guards arbitrary already-submitted routes,
// it has no reference to VerificationPage's capture/upload functions).
//
// Mirrors SuspendedAccountPage.js's existing card/icon/title/message/
// button shape and i18n defaultValue convention, rather than inventing a
// new visual language for an account-blocked state.
export default function AccountErrorRecovery({ onRetry }) {
  const { t } = useTranslation();
  const [retrying, setRetrying] = useState(false);
  // Belt-and-suspenders against a double-invocation racing past the
  // `disabled` attribute (e.g. a fast double-tap before the first
  // setState-driven re-render commits) - the same pattern VerificationPage
  // already uses (submitInFlightRef) for its own single-submission guard.
  const retryInFlightRef = useRef(false);

  const handleRetry = async () => {
    if (retryInFlightRef.current) return;
    retryInFlightRef.current = true;
    setRetrying(true);
    try {
      if (typeof onRetry === "function") {
        await onRetry();
      }
    } finally {
      retryInFlightRef.current = false;
      setRetrying(false);
    }
  };

  return (
    <div className="account-error-recovery">
      <div className="account-error-recovery__body">
        <div className="account-error-recovery-card">
          <div className="account-error-recovery-card__icon" aria-hidden>
            ⚠️
          </div>

          <h1 className="account-error-recovery-card__title">
            {t("account.refresh_error_title", {
              defaultValue: "Something went wrong",
            })}
          </h1>

          <p className="account-error-recovery-card__message">
            {t("account.refresh_error_message", {
              defaultValue:
                "We couldn't load your account. Check your connection and try again.",
            })}
          </p>

          <button
            type="button"
            className="account-error-recovery-card__btn"
            onClick={handleRetry}
            disabled={retrying}
          >
            {retrying
              ? t("common.loading", { defaultValue: "Loading..." })
              : t("account.refresh_error_retry", { defaultValue: "Try again" })}
          </button>
        </div>
      </div>
    </div>
  );
}
